import { useEffect, useRef, useState } from 'react';
import { id } from '@instantdb/react';
import { db } from '../db/client';
import type { AnchoredSelector } from '../shared/selector';
import { avatarColor, avatarInitial, displayName } from '../widget/avatar';

export type ComposerTarget = {
  selector: AnchoredSelector;
  rect: DOMRect;
  clickX: number;
  clickY: number;
};

export function CommentComposer({
  target,
  orgId,
  userId,
  userEmail,
  onClose,
}: {
  target: ComposerTarget;
  orgId: string;
  userId: string;
  userEmail: string;
  onClose: () => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  async function save() {
    if (!body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const commentId = id();
      const u = new URL(window.location.href);
      const pageOrigin = u.origin;
      const pagePath = u.pathname;
      const pageUrl = pageOrigin + pagePath; // strip query/hash so reloads match
      await db.transact([
        db.tx.comments[commentId].update({
          pageUrl,
          pageOrigin,
          pagePath,
          pageTitle: document.title.slice(0, 200),
          cssSelector: target.selector.cssSelector,
          xpath: target.selector.xpath,
          elementText: target.selector.elementText,
          pinX: target.selector.pinX,
          pinY: target.selector.pinY,
          body: body.trim(),
          status: 'open',
          authorEmail: userEmail.trim().toLowerCase(),
          createdAt: Date.now(),
        }),
        db.tx.comments[commentId].link({ organization: orgId, author: userId }),
      ]);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const CARD_WIDTH = 360;
  const ESTIMATED_HEIGHT = 180;
  const MARGIN = 12;

  const viewportTop = window.scrollY;
  const viewportBottom = window.scrollY + window.innerHeight;
  const spaceBelow = viewportBottom - target.clickY;
  const placeAbove = spaceBelow < ESTIMATED_HEIGHT + MARGIN;

  const top = placeAbove
    ? Math.max(viewportTop + MARGIN, target.clickY - ESTIMATED_HEIGHT - MARGIN)
    : target.clickY + MARGIN;

  const left = Math.min(
    Math.max(window.scrollX + MARGIN, target.clickX - 20),
    window.scrollX + window.innerWidth - CARD_WIDTH - MARGIN,
  );

  const me = avatarColor(userEmail);
  const canSubmit = body.trim().length > 0 && !busy;

  return (
    <div
      className="uin-composer"
      style={{ top, left, width: CARD_WIDTH }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="uin-composer-head">
        <div className="uin-composer-avatar" style={{ background: me.bg, color: me.fg }}>
          {avatarInitial(userEmail)}
        </div>
        <div className="uin-composer-meta">
          <span className="uin-composer-name">{displayName(userEmail)}</span>
          <span className="uin-composer-sub">New comment</span>
        </div>
        <button
          className="uin-icon-btn"
          aria-label="Cancel"
          title="Cancel (Esc)"
          onClick={onClose}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <textarea
        ref={textareaRef}
        className="uin-composer-textarea"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a comment…"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            save();
          }
          if (e.key === 'Escape') onClose();
        }}
      />

      {err && <div className="uin-composer-error">{err}</div>}

      <div className="uin-composer-actions">
        <button className="uin-btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="uin-btn-primary-pill"
          onClick={save}
          disabled={!canSubmit}
        >
          {busy ? 'Saving…' : 'Comment'}
        </button>
      </div>
    </div>
  );
}
